import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
});

let getAccessToken: () => string | null = () => null;
let setAccessToken: (token: string | null) => void = () => undefined;
let onLogout: () => Promise<void> | void = () => undefined;

export function configureApi(opts: {
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  onLogout: () => Promise<void> | void;
}): void {
  getAccessToken = opts.getAccessToken;
  setAccessToken = opts.setAccessToken;
  onLogout = opts.onLogout;
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await axios.post<{ accessToken: string }>(
        `${baseURL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const token = res.data?.accessToken ?? null;
      setAccessToken(token);
      return token;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as RetriableConfig;
    const status = error.response?.status;
    if (status === 401 && !original._retry) {
      original._retry = true;
      const newToken = await performRefresh();
      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      await onLogout();
    }
    return Promise.reject(error);
  },
);
